import { AgentDiscoveryError, mapBackendError } from './errors.js';
import type {
  BackendEnvelope,
  ChallengeData,
  KSignConfig,
  LoginOptions,
  LoginResult,
  WebSocketConstructor,
} from './types.js';
import { discoverAgent, resolveOrigin, resolveWebSocketImpl } from './discovery.js';
import { resolveFetchImpl } from './fetch.js';
import { runLoginSession } from './login-session.js';
import { newMessageId } from './ws-envelope.js';

function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

async function parseEnvelope<T>(response: Response): Promise<T> {
  let body: BackendEnvelope<T>;
  try {
    body = (await response.json()) as BackendEnvelope<T>;
  } catch {
    throw mapBackendError({
      code: response.status || 1000,
      message: `invalid backend response (HTTP ${response.status})`,
    });
  }
  if (!body.success) {
    throw mapBackendError(body);
  }
  return body.data;
}

/**
 * Official KSign browser helper — Mode A WebSocket login orchestration (v2.3).
 *
 * Does not return or store KSign JWT, signatures, or certificates.
 */
export class KSign {
  private readonly publishableKey: string;
  private readonly backendUrl: string;
  private readonly origin: string;
  private readonly agentPort: number;
  private readonly fetchImpl: typeof fetch;
  private readonly WebSocketImpl: WebSocketConstructor;
  private readonly createWebSocket?: (url: string) => import('./types.js').WebSocketLike;

  constructor(config: KSignConfig) {
    if (!config.publishableKey?.trim()) {
      throw new Error('publishableKey is required');
    }
    if (!config.backendUrl?.trim()) {
      throw new Error('backendUrl is required');
    }
    this.publishableKey = config.publishableKey.trim();
    this.backendUrl = trimTrailingSlash(config.backendUrl);
    this.origin = resolveOrigin(config.origin);
    this.agentPort = config.agentPort ?? 57601;
    this.fetchImpl = resolveFetchImpl(config.fetch);
    this.WebSocketImpl = resolveWebSocketImpl(config.WebSocketImpl);
    this.createWebSocket = config.createWebSocket;
  }

  /**
   * Full e-sign login:
   * discoverAgent → POST challenge → WebSocket LOGIN_START… → LOGIN_SUCCESS
   */
  async login(options: LoginOptions): Promise<LoginResult> {
    const pinMode = options.pinMode ?? 'browser';
    if (pinMode !== 'browser' && pinMode !== 'agent') {
      throw new Error('pinMode must be browser or agent');
    }
    if (pinMode === 'browser') {
      const hasPin = typeof options.pin === 'string' && options.pin.length > 0;
      if (!hasPin && !options.onRequestPin && !options.onPinRetry) {
        throw new Error('pin is required, or provide onRequestPin / onPinRetry');
      }
    }

    let discovery;
    try {
      discovery = await discoverAgent(this.agentPort, this.fetchImpl);
    } catch (error) {
      if (error instanceof AgentDiscoveryError) {
        throw error;
      }
      throw new AgentDiscoveryError();
    }

    const challenge = await this.createChallenge({
      payload: options.payload ?? '',
      correlationId: options.correlationId ?? newMessageId(),
    });

    return runLoginSession({
      wsLoginUrl: discovery.wsLoginUrl,
      challenge,
      origin: this.origin,
      options,
      WebSocketImpl: this.WebSocketImpl,
      createWebSocket: this.createWebSocket,
    });
  }

  private async createChallenge(input: {
    payload: string;
    correlationId: string;
  }): Promise<ChallengeData> {
    const response = await this.fetchImpl(`${this.backendUrl}/api/v1/auth/esign/challenge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-KSign-Publishable-Key': this.publishableKey,
        Origin: this.origin,
        Accept: 'application/json',
      },
      body: JSON.stringify({
        purpose: 'login',
        payload: input.payload,
        correlation_id: input.correlationId,
      }),
    });
    const data = await parseEnvelope<ChallengeData>(response);
    if (!data.agent_ticket || !data.challenge_id || !data.nonce || !data.payload_hash) {
      throw mapBackendError({ code: 1000, message: 'incomplete challenge response' });
    }
    return data;
  }
}
